// ===================================================================
// KAIXO - InlineComments
// ===================================================================
// Sección de comentarios inline DENTRO de un post del feed. Contiene
// la lista de comentarios, traducciones bajo demanda, likes y input
// para añadir uno. Se muestra solo cuando el usuario toca el botón
// de comentarios del post.
// ===================================================================

import React, { useState } from 'react';
import { Icons } from '../Icons/Icons';
import EmojiPicker from '../EmojiPicker/EmojiPicker';
import ct from './i18n';

export default function InlineComments({
    messageId,
    comments,                 // array | 'loading' | undefined
    commentsEnabled,          // bool
    isAuthor,                 // true si el viewer es el dueño del canal
    currentUserId,
    draft,                    // texto actual del input
    onDraftChange,            // (value) => void
    onSubmit,                 // () => void
    commentTranslations,      // { [cid]: { text, loading, error } }
    commentLikes,             // { [cid]: { likesCount, likedByMe } }
    onToggleTranslate,        // (comment) => void
    onToggleLike,             // (comment) => void
    onDelete                  // (messageId, commentId) => void
}) {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    if (!comments) return null;
    if (comments === 'loading') {
        return (
            <div className="v2-inline-comments">
                <p className="v2-center v2-dim">{ct.loading}</p>
            </div>
        );
    }

    return (
        <div className="v2-inline-comments">
            {comments.length === 0 && (
                <p className="v2-dim v2-center" style={{ padding: '8px 0' }}>
                    {ct.noComments || 'Sin comentarios todavía'}
                </p>
            )}
            {comments.map(c => {
                const canDelete = isAuthor
                    || String(c.author?._id) === String(currentUserId)
                    || String(c.author) === String(currentUserId);
                const cid = String(c._id);
                const tr = commentTranslations?.[cid];
                const likeState = commentLikes?.[cid] || {
                    likesCount: c.likesCount || 0,
                    likedByMe: !!c.likedByMe
                };
                return (
                    <div key={c._id} className="v2-inline-comment">
                        <div className="v2-inline-comment-avatar">
                            {c.author?.avatar
                                ? <img src={c.author.avatar} alt="" loading="lazy" decoding="async" />
                                : ((c.author?.name || '?')[0] || '?').toUpperCase()}
                        </div>
                        <div className="v2-inline-comment-body">
                            <span
                                className="v2-inline-comment-text"
                                onClick={() => onToggleTranslate(c)}
                                title={ct.tapToTranslate || 'Toca para traducir'}
                                style={{ cursor: 'pointer' }}
                            >
                                <strong>{c.author?.name || '—'}</strong> {c.text}
                            </span>
                            {tr?.loading && (
                                <span className="v2-inline-comment-tr v2-dim">
                                    {ct.translating || 'Traduciendo…'}
                                </span>
                            )}
                            {tr?.text && !tr.loading && (
                                <span className="v2-inline-comment-tr">
                                    🌐 {tr.text}
                                </span>
                            )}
                            {tr?.error && (
                                <span className="v2-inline-comment-tr v2-dim">{tr.error}</span>
                            )}
                            <span className="v2-inline-comment-time">
                                {new Date(c.createdAt).toLocaleString()}
                            </span>
                        </div>
                        <button
                            className={`v2-inline-comment-like ${likeState.likedByMe ? 'liked' : ''}`}
                            onClick={() => onToggleLike(c)}
                            aria-label="Me gusta"
                        >
                            <span className="v2-inline-comment-like-icon">
                                {likeState.likedByMe ? '❤️' : '🤍'}
                            </span>
                            {likeState.likesCount > 0 && (
                                <span className="v2-inline-comment-like-count">
                                    {likeState.likesCount}
                                </span>
                            )}
                        </button>
                        {canDelete && (
                            <button
                                className="v2-inline-comment-del"
                                onClick={() => onDelete(messageId, c._id)}
                                aria-label="Eliminar"
                            >
                                <Icons.Trash size={13} />
                            </button>
                        )}
                    </div>
                );
            })}
            {commentsEnabled !== false && (
                <div className="v2-inline-comment-input">
                    <input
                        value={draft || ''}
                        onChange={e => onDraftChange(e.target.value)}
                        placeholder={ct.addComment || 'Añadir comentario...'}
                        maxLength={500}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                onSubmit();
                            }
                        }}
                    />
                    <div className="kx-emoji-wrapper">
                        <button
                            type="button"
                            className="kx-emoji-trigger"
                            onClick={() => setShowEmojiPicker(v => !v)}
                            aria-label="Insertar emoji"
                            title="Insertar emoji"
                        >😊</button>
                        <EmojiPicker
                            open={showEmojiPicker}
                            onPick={(em) => {
                                onDraftChange((draft || '') + em);
                                setShowEmojiPicker(false);
                            }}
                            onClose={() => setShowEmojiPicker(false)}
                        />
                    </div>
                    <button
                        className="v2-inline-send-btn"
                        disabled={!(draft || '').trim()}
                        onClick={onSubmit}
                    >
                        {ct.publish || 'Publicar'}
                    </button>
                </div>
            )}
        </div>
    );
}
